import { apiClient, ApiError } from './client';
import type { components } from './schema';

/** One record that matched. A value it has none of is null, never missing. */
export type SearchHit = components['schemas']['SearchHit'];

/** The matches of one type, and whether more matched than they hold. */
export type SearchGroup = components['schemas']['SearchGroup'];

/** What a search found: only the types with hits, in the order the server lists them. */
export type SearchResults = components['schemas']['SearchResults'];

export type SearchType = SearchHit['type'];

/** The record types one search covers, in the order the server lists their groups. */
export const SEARCH_TYPES = [
  'CUSTOMER',
  'JOB',
  'DEAL',
  'LEAD',
  'PROPOSAL',
  'INVOICE',
  'MATERIAL',
  'SERVICE',
  'PERSON',
] as const satisfies readonly SearchType[];

/** Fewer characters than this and the server answers with no groups, so nothing is sent. */
export const MIN_SEARCH_LENGTH = 2;

/** More characters than this and the server refuses the request. */
export const MAX_SEARCH_LENGTH = 100;

export interface SearchOptions {
  /** Narrows the search to these types; every type the person may read when left out. */
  readonly types?: readonly SearchType[];
  /** How many hits per type, up to the server's maximum of 20. */
  readonly limit?: number;
  /** Cancels the request, so an answer nobody wants any more costs nothing. */
  readonly signal?: AbortSignal;
}

/** The server's own explanation from a problem body, or the status when it gave none. */
function failure(problem: unknown, status: number): ApiError {
  const detail =
    typeof problem === 'object' && problem !== null && 'detail' in problem
      ? problem.detail
      : undefined;
  return new ApiError(
    status,
    typeof detail === 'string' && detail.trim() !== ''
      ? detail
      : `The request failed (${String(status)})`,
  );
}

/**
 * Searches every kind of record at once.
 *
 * @param query what was typed; trimmed, and cut to the longest the server accepts
 * @param options the types, how many of each, and a way to cancel
 * @returns the hits grouped by type; nothing at all for a query shorter than two characters
 * @throws ApiError when the server refuses
 */
export async function searchEverything(
  query: string,
  { types, limit, signal }: SearchOptions = {},
): Promise<SearchResults> {
  const q = query.trim().slice(0, MAX_SEARCH_LENGTH).trim();
  if (q.length < MIN_SEARCH_LENGTH) {
    return { query: q, groups: [] };
  }
  const { data, error, response } = await apiClient.GET('/api/search', {
    params: {
      query: {
        q,
        ...(types === undefined || types.length === 0 ? {} : { types: types.join(',') }),
        ...(limit === undefined ? {} : { limit }),
      },
    },
    ...(signal === undefined ? {} : { signal }),
  });
  if (data === undefined) {
    throw failure(error, response.status);
  }
  return data;
}
